'use client'

import { authFetch } from '@/lib/auth/auth-fetch'
import { useWallet } from '@/providers/WalletContext'
import { useInviteStore } from '@/stores/invite-store'
import { useCallback, useEffect, useRef } from 'react'

// Key for storing referral code from /r/[code] visits
const REFERRAL_CODE_KEY = 'referral_code'
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 3000

export function clearPendingReferral() {
	localStorage.removeItem(REFERRAL_CODE_KEY)
}

export function setReferralCode(code: string) {
	localStorage.setItem(REFERRAL_CODE_KEY, code)
}

export function getReferralCode(): string | null {
	if (typeof window === 'undefined') return null
	return localStorage.getItem(REFERRAL_CODE_KEY)
}

export function useRegisterUser() {
	const { eoaAddress, authenticated } = useWallet()
	const isProcessingRef = useRef(false)
	const lastProcessedAddressRef = useRef<string | null>(null)
	const retryCountRef = useRef(0)
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const showInviteModal = useInviteStore(s => s.showInviteModal)

	// Reset when user logs out
	useEffect(() => {
		if (!authenticated) {
			lastProcessedAddressRef.current = null
			isProcessingRef.current = false
			retryCountRef.current = 0
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current)
				retryTimerRef.current = null
			}
		}
	}, [authenticated])

	const attemptRegistration = useCallback(async () => {
		if (isProcessingRef.current) return
		if (!(authenticated && eoaAddress)) return

		isProcessingRef.current = true

		try {
			const referralCode = localStorage.getItem(REFERRAL_CODE_KEY)

			const res = await authFetch('/api/auth/register', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					referralCode: referralCode || undefined,
				}),
			})

			const data = await res.json()

			// New user needs an invite code - show the modal
			if (data.needsInviteCode) {
				showInviteModal()
				isProcessingRef.current = false
				return
			}

			// Handle errors — schedule retry if possible
			if (!res.ok) {
				console.error('Registration failed:', data.error)
				isProcessingRef.current = false
				scheduleRetry()
				return
			}

			// Success
			lastProcessedAddressRef.current = eoaAddress
			isProcessingRef.current = false
			retryCountRef.current = 0

			if (data.isNew) {
				clearPendingReferral()
			}
		} catch (err) {
			console.error('Failed to register user:', err)
			isProcessingRef.current = false
			scheduleRetry()
		}
	}, [authenticated, eoaAddress, showInviteModal])

	const scheduleRetry = useCallback(() => {
		if (retryCountRef.current >= MAX_RETRIES) return

		retryCountRef.current += 1
		const delay = RETRY_DELAY_MS * retryCountRef.current // Increasing delay

		retryTimerRef.current = setTimeout(() => {
			retryTimerRef.current = null
			attemptRegistration()
		}, delay)
	}, [attemptRegistration])

	// biome-ignore lint/correctness/useExhaustiveDependencies: Need authenticated+eoaAddress to re-trigger after wallet session retry
	useEffect(() => {
		// Skip if already processed this address
		if (lastProcessedAddressRef.current === eoaAddress) return

		attemptRegistration()
	}, [authenticated, eoaAddress, attemptRegistration])

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current)
			}
		}
	}, [])
}
